import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { type FC, useCallback } from "react";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";

interface AttachmentEditorToolbarProps {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  onClose: () => void;
}

export const AttachmentEditorToolbar: FC<AttachmentEditorToolbarProps> = ({
  hasUnsavedChanges,
  isSaving,
  onSave,
  onDiscard,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  const { renderConfirmationModal, onConfirmThenClose } = useConfirmationModal({
    needsConfirmation: hasUnsavedChanges,
    onClose,
  });

  const handleSave = useCallback(async () => {
    try {
      await onSave();
      showNotification(dispatch, {
        id: `attachment-save-${Date.now().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Article saved",
        secondaryMessage: "Your changes have been saved.",
      });
    } catch {
      showNotification(dispatch, {
        id: `attachment-save-error-${Date.now().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to save article",
        secondaryMessage: "Please try again.",
      });
    }
  }, [onSave, dispatch]);

  const {
    renderConfirmationModal: renderSaveModal,
    onConfirmThenClose: onConfirmThenSave,
  } = useConfirmationModal({ needsConfirmation: true, onClose: handleSave });

  return (
    <>
      {renderConfirmationModal({
        title: "Discard changes?",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmText: "Discard",
      })}
      {renderSaveModal({
        title: "Save changes?",
        message: "Your changes will be reflected on the agent immediately.",
        confirmText: "Save",
      })}
      <div className="flex items-center justify-end border-b border-border-light bg-background px-6 py-3">
        <SaveAndCancelButtons
          onSave={onConfirmThenSave}
          onCancel={onConfirmThenClose}
          isSaving={isSaving}
          isSaveDisabled={!hasUnsavedChanges}
        />
      </div>
    </>
  );
};
